import { ChevronDown, Globe, Lock, Users } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";

/** Managers-only content is visible to admins only. */
export function useCanManageAdminOnly() {
  const { role } = useAuth();
  return role === "admin";
}

export function ManagersOnlyBadge({ className }: { className?: string }) {
  return (
    <Badge
      variant="outline"
      className={cn(
        "gap-1 font-normal text-amber-700 dark:text-amber-400",
        className,
      )}
      data-testid="managers-only-badge"
    >
      <Lock className="h-3 w-3" />
      Managers only
    </Badge>
  );
}

export type MeetingNoteScope = "managers" | "employees" | "all";

/** Managers if admin-only, else employees if hidden from collaborators. */
export function meetingNoteScope(note: {
  admin_only?: boolean | null;
  hidden_from_collaborators?: boolean | null;
}): MeetingNoteScope {
  if (note.admin_only) return "managers";
  if (note.hidden_from_collaborators) return "employees";
  return "all";
}

/** The note flags for a scope. Managers notes are hidden from collaborators
 *  too, so lifting managers-only never exposes them by accident. */
export function meetingNoteScopeFlags(scope: MeetingNoteScope) {
  return {
    admin_only: scope === "managers",
    hidden_from_collaborators: scope !== "all",
  };
}

const SCOPE_OPTIONS: Array<{
  value: MeetingNoteScope;
  label: string;
  description: string;
  icon: typeof Lock;
}> = [
  {
    value: "managers",
    label: "Managers",
    description: "Admins only",
    icon: Lock,
  },
  {
    value: "employees",
    label: "Employees",
    description: "Admins and employees, not project collaborators",
    icon: Users,
  },
  {
    value: "all",
    label: "All",
    description: "Everyone incl. the project's freelance collaborators",
    icon: Globe,
  },
];

export function EmployeesOnlyBadge({ className }: { className?: string }) {
  return (
    <Badge
      variant="outline"
      className={cn(
        "gap-1 font-normal text-sky-700 dark:text-sky-400",
        className,
      )}
      data-testid="employees-only-badge"
    >
      <Users className="h-3 w-3" />
      Employees only
    </Badge>
  );
}

/** Badge for non-default meeting note scopes; nothing for "All". */
export function MeetingNoteScopeBadge({
  note,
  className,
}: {
  note: {
    admin_only?: boolean | null;
    hidden_from_collaborators?: boolean | null;
  };
  className?: string;
}) {
  const scope = meetingNoteScope(note);
  if (scope === "managers") return <ManagersOnlyBadge className={className} />;
  if (scope === "employees") {
    return <EmployeesOnlyBadge className={className} />;
  }
  return null;
}

/**
 * Picks who sees a meeting note: Managers (admins only), Employees (staff,
 * not project collaborators) or All. Staff only; Managers is offered to
 * admins only. Freelancers never get the selector.
 */
export function MeetingNoteScopeSelect({
  scope,
  onChange,
  disabled,
  className,
}: {
  scope: MeetingNoteScope;
  onChange: (scope: MeetingNoteScope) => void;
  disabled?: boolean;
  className?: string;
}) {
  const { isEmployee } = useAuth();
  const canManageAdminOnly = useCanManageAdminOnly();
  if (!isEmployee) return null;

  const options = SCOPE_OPTIONS.filter(
    (option) => option.value !== "managers" || canManageAdminOnly,
  );
  const current =
    SCOPE_OPTIONS.find((option) => option.value === scope) ?? SCOPE_OPTIONS[2];
  const Icon = current.icon;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={cn(
            "h-7",
            scope === "managers" &&
              "border-amber-500/60 text-amber-700 dark:text-amber-400",
            scope === "employees" &&
              "border-sky-500/60 text-sky-700 dark:text-sky-400",
            className,
          )}
          disabled={disabled || (scope === "managers" && !canManageAdminOnly)}
          aria-label={`Visibility: ${current.label}`}
          title={current.description}
          data-testid="meeting-note-scope-select"
          data-scope={scope}
        >
          <Icon className="h-3 w-3 mr-1" />
          {current.label}
          <ChevronDown className="h-3 w-3 ml-1 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuLabel>Visible to</DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={scope}
          onValueChange={(value) => {
            if (value !== scope) onChange(value as MeetingNoteScope);
          }}
        >
          {options.map((option) => (
            <DropdownMenuRadioItem
              key={option.value}
              value={option.value}
              data-testid={`meeting-note-scope-${option.value}`}
            >
              <div className="flex flex-col">
                <span className="flex items-center gap-1.5">
                  <option.icon className="h-3 w-3" />
                  {option.label}
                </span>
                <span className="text-xs text-muted-foreground">
                  {option.description}
                </span>
              </div>
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
